/** Classes — who in this school studies what.
 *
 * The school buys a level once; here the admin decides which pupils get it.
 * Assign to a CLASS and every member inherits it (the bulk lever a school
 * with 200 pupils needs), then override for the individual exception.
 *
 * Only modules the school actually owns are offered, so an admin can't hand
 * out a level that was never bought.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/classes";
import type { LearnerAccess, SchoolClass } from "../../api/classes";
import { listTeam } from "../../api/team";
import type { TeamMember } from "../../api/types";
import { Button, Card, EmptyState, Input, Label, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

/** Checkbox list of the modules the school owns. */
function ModulePicker({
  available,
  selected,
  onToggle,
  idPrefix,
}: {
  available: string[];
  selected: string[];
  onToggle: (code: string) => void;
  idPrefix: string;
}) {
  const t = useT();
  if (available.length === 0) {
    return <p className="text-sm text-muted">{t("classes.noModules")}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {available.map((code) => {
        const on = selected.includes(code);
        return (
          <label
            key={code}
            htmlFor={`${idPrefix}-${code}`}
            className={`cursor-pointer rounded-control border px-3 py-1.5 text-sm font-semibold ${
              on ? "border-brand bg-brand text-white" : "border-line bg-canvas text-ink hover:border-brand"
            }`}
          >
            <input
              id={`${idPrefix}-${code}`}
              type="checkbox"
              className="sr-only"
              checked={on}
              onChange={() => onToggle(code)}
            />
            {api.moduleLabel(code)}
          </label>
        );
      })}
    </div>
  );
}

export function ClassesPage() {
  const t = useT();
  const [classes, setClasses] = useState<SchoolClass[] | null>(null);
  const [pupils, setPupils] = useState<TeamMember[]>([]);
  const [available, setAvailable] = useState<string[]>([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // per-pupil override panel
  const [pupilId, setPupilId] = useState("");
  const [access, setAccess] = useState<LearnerAccess | null>(null);

  const loadClasses = () => api.listClasses().then(setClasses).catch(() => setClasses([]));

  useEffect(() => {
    loadClasses();
    listTeam()
      .then((rows) => setPupils(rows.filter((m) => m.role === "learner")))
      .catch(() => setPupils([]));
    api.assignableModules().then(setAvailable).catch(() => setAvailable([]));
  }, []);

  useEffect(() => {
    if (!pupilId) {
      setAccess(null);
      return;
    }
    api.getLearnerAccess(pupilId).then(setAccess).catch(() => setAccess(null));
  }, [pupilId]);

  if (!classes) return <Spinner />;

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.createClass(newName.trim());
      setNewName("");
      await loadClasses();
    } finally {
      setBusy(false);
    }
  };

  const toggleModule = async (klass: SchoolClass, code: string) => {
    const next = klass.module_codes.includes(code)
      ? klass.module_codes.filter((c) => c !== code)
      : [...klass.module_codes, code];
    setNote(null);
    await api.setClassModules(klass.id, next);
    await loadClasses();
    setNote(t("classes.saved"));
  };

  const toggleMember = async (klass: SchoolClass, userId: string) => {
    const next = klass.member_ids.includes(userId)
      ? klass.member_ids.filter((id) => id !== userId)
      : [...klass.member_ids, userId];
    setNote(null);
    await api.setClassMembers(klass.id, next);
    await loadClasses();
    setNote(t("classes.saved"));
  };

  const toggleDirect = async (code: string) => {
    if (!access) return;
    const next = access.direct_modules.includes(code)
      ? access.direct_modules.filter((c) => c !== code)
      : [...access.direct_modules, code];
    setAccess(await api.setLearnerModules(access.user_id, next));
    setNote(t("classes.saved"));
  };

  const remove = async (klass: SchoolClass) => {
    await api.deleteClass(klass.id);
    await loadClasses();
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold text-ink">{t("classes.title")}</h1>
        <p className="mt-1 max-w-2xl text-muted">{t("classes.subtitle")}</p>
      </div>

      {note && <p role="status" className="text-sm font-semibold text-brand">{note}</p>}

      <Card title={t("classes.newClass")}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1">
            <Label htmlFor="class-name">{t("classes.name")}</Label>
            <Input
              id="class-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t("classes.namePlaceholder")}
            />
          </div>
          <Button onClick={add} loading={busy} disabled={!newName.trim()}>
            {t("classes.create")}
          </Button>
        </div>
      </Card>

      {classes.length === 0 ? (
        <Card><EmptyState message={t("classes.empty")} /></Card>
      ) : (
        classes.map((klass) => (
          <Card
            key={klass.id}
            title={klass.name}
            actions={
              <button
                onClick={() => remove(klass)}
                className="text-sm font-semibold text-muted hover:text-danger"
              >
                {t("classes.delete")}
              </button>
            }
          >
            <p className="text-xs font-semibold uppercase text-muted">
              {t("classes.modules")}
            </p>
            <div className="mt-2">
              <ModulePicker
                available={available}
                selected={klass.module_codes}
                onToggle={(code) => toggleModule(klass, code)}
                idPrefix={`cls-${klass.id}`}
              />
            </div>

            <p className="mt-4 text-xs font-semibold uppercase text-muted">
              {t("classes.members", { count: klass.member_ids.length })}
            </p>
            {pupils.length === 0 ? (
              <p className="mt-2 text-sm text-muted">{t("classes.noPupils")}</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {pupils.map((p) => {
                  const on = klass.member_ids.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      htmlFor={`mem-${klass.id}-${p.id}`}
                      className={`cursor-pointer rounded-control border px-3 py-1.5 text-sm ${
                        on ? "border-brand bg-brand-soft font-semibold text-brand-dark" : "border-line text-ink hover:border-brand"
                      }`}
                    >
                      <input
                        id={`mem-${klass.id}-${p.id}`}
                        type="checkbox"
                        className="sr-only"
                        checked={on}
                        onChange={() => toggleMember(klass, p.id)}
                      />
                      {p.full_name}
                    </label>
                  );
                })}
              </div>
            )}
          </Card>
        ))
      )}

      <Card title={t("classes.individual")}>
        <p className="mb-3 text-sm text-muted">{t("classes.individualBody")}</p>
        <Label htmlFor="pupil">{t("classes.pupil")}</Label>
        <select
          id="pupil"
          value={pupilId}
          onChange={(e) => setPupilId(e.target.value)}
          className="w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
        >
          <option value="">{t("classes.choosePupil")}</option>
          {pupils.map((p) => (
            <option key={p.id} value={p.id}>{p.full_name}</option>
          ))}
        </select>

        {access && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase text-muted">
              {t("classes.extraModules")}
            </p>
            <div className="mt-2">
              <ModulePicker
                available={available}
                selected={access.direct_modules}
                onToggle={toggleDirect}
                idPrefix="direct"
              />
            </div>
            <p className="mt-3 text-sm text-muted">
              {t("classes.effective")}:{" "}
              <span className="font-semibold text-ink">
                {access.effective_modules.length === 0
                  ? t("classes.nothingYet")
                  : access.effective_modules.map(api.moduleLabel).join(", ")}
              </span>
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
